<template>
  <a href="#" @click.prevent="generatePdf" class="action-link">
    <img src="/images/featured/document-download-morado.svg" alt="download" />
    <span v-if="isGenerating">Generando PDF...</span>
    <span v-else>Descargar como PDF</span>
  </a>
</template>

<script>
import { getRandomColor } from '@/constants/colors';
import { formatFullCourseName } from '@/utils/subjectNameUtils';
import { getLocationDisplayName } from '@/utils/locationName';

export default {
  name: 'PurchasePdfGenerator',
  props: {
    order: { type: Object, required: false, default: null },
    students: { type: Array, required: true },
    financingParty: { type: Object, required: true }
  },
  data() {
    return {
      isGenerating: false
    }
  },
  
  mounted() {
  },

  methods: {
    async generatePdf() {
      // Validar que order esté disponible
      if (!this.order) {
        alert('No hay información de orden disponible para generar el PDF');
        return;
      }
      
      this.isGenerating = true;
      
      try {
        // Crear el HTML del PDF desde cero
        const pdfHTML = this.createPdfHTML();
        
        // Crear un elemento temporal para el PDF
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = pdfHTML;
        document.body.appendChild(tempContainer);
        
        // Configurar opciones del PDF
        const options = {
          margin: [20, 15, 30, 15],
          filename: 'comprobante-pago-pdv.pdf',
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { 
            scale: 2,
            useCORS: true,
            allowTaint: true
          },
          jsPDF: { 
            unit: "mm", 
            format: "a4", 
            orientation: "portrait" 
          },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
        };

        // Carga dinámica para evitar traer fast-png/jspdf en el arranque
        const { default: html2pdf } = await import(/* webpackChunkName: "html2pdf" */ 'html2pdf.js');
        await html2pdf().set(options).from(tempContainer.firstElementChild).save();
        
        // Limpiar el elemento temporal
        document.body.removeChild(tempContainer);
        
      } catch (error) {
        console.error('Error generando PDF:', error);
      } finally {
        this.isGenerating = false;
      }
    },

    createPdfHTML() {
      if (!this.students || this.students.length === 0) {
        console.error('No hay datos de estudiantes disponibles');
        return '<div>Error: No hay datos disponibles</div>';
      }
      
      if (!this.order) {
        console.error('No hay datos de orden disponibles');
        return '<div>Error: No hay datos de orden disponibles</div>';
      }
      
      const student = this.students[0];
      const orderData = this.order;
      
      // Obtener información del estudiante con validaciones
      const name = student?.name || '';
      const fatherLastName = student?.fatherLastName || '';
      const motherLastName = student?.motherLastName || '';
      
      // Construir nombre completo concatenando nombre y apellidos
      let studentName = name.trim();
      if (fatherLastName) {
        studentName += ' ' + fatherLastName.trim();
      }
      if (motherLastName) {
        studentName += ' ' + motherLastName.trim();
      }
      studentName = studentName.trim() || 'Estudiante no especificado';
      
      // Obtener sede correctamente (misma lógica que PurchaseConfirmation.vue)
      const locationName = this.getStudentLocation();
      
      // Obtener cursos desde OrderDetails (misma lógica que PurchaseConfirmation)
      const selectedCourses = this.getStudentCoursesFromOrderDetails();
      
      // Calcular pricing
      const pricing = this.calculatePricing();

      return `
        <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; background: white;">
          <!-- Header -->
          <div style="background: #FFD700; padding: 8px; text-align: center; margin-bottom: 20px; border-radius: 4px;">
            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
              viewBox="0 0 190 76" style="enable-background:new 0 0 190 76; width: 140px; height: 56px;" xml:space="preserve">
              <style type="text/css">
                .st0{fill:#020203;}
                .st1{fill:#FFD100;}
              </style>
              <g>
                <g>
                  <g>
                    <rect x="1" y="53.82" class="st0" width="188" height="2.82"/>
                    <rect x="1" y="29.82" class="st0" width="188" height="22.57"/>
                    <g>
                      <path class="st1" d="M4.55,35.62H8.3c0.61,0,1.17,0.07,1.7,0.21c0.53,0.14,0.98,0.35,1.37,0.62c0.39,0.28,0.7,0.62,0.92,1.04
                          c0.22,0.42,0.34,0.9,0.34,1.45c0,0.56-0.11,1.04-0.33,1.46c-0.22,0.42-0.51,0.76-0.87,1.04c-0.36,0.28-0.77,0.48-1.24,0.62
                          c-0.46,0.14-0.94,0.21-1.42,0.21H7.2v5.15H4.55V35.62z M7.2,41.19h0.86c0.3,0,0.56-0.06,0.78-0.18
                          c0.22-0.12,0.41-0.28,0.56-0.49c0.15-0.21,0.26-0.44,0.33-0.71c0.07-0.27,0.11-0.55,0.11-0.84c0-0.35-0.05-0.67-0.14-0.94
                          c-0.1-0.28-0.23-0.51-0.4-0.7c-0.17-0.19-0.37-0.33-0.59-0.43c-0.23-0.1-0.47-0.15-0.73-0.15H7.2V41.19z"/>
                      <polygon class="st1" points="23.76,35.62 23.76,36.62 18.36,36.62 18.36,40.51 22.84,40.51 22.84,41.52 18.36,41.52 18.36,46.2 
                      23.76,46.2 23.76,47.41 15.72,47.41 15.72,35.62 				"/>
                      <path class="st1" d="M26.84,35.62h3.91c1,0,1.93,0.13,2.77,0.38c0.84,0.26,1.57,0.63,2.17,1.13c0.61,0.49,1.08,1.1,1.42,1.82
                          c0.34,0.72,0.51,1.54,0.51,2.46c0,0.83-0.14,1.61-0.43,2.34c-0.29,0.73-0.7,1.37-1.25,1.91c-0.55,0.54-1.22,0.97-2.02,1.28
                          c-0.8,0.31-1.71,0.47-2.75,0.47h-4.32V35.62z M29.47,46.2h1.41c0.52,0,1.01-0.1,1.47-0.31c0.46-0.21,0.86-0.52,1.2-0.92
                          c0.34-0.41,0.61-0.9,0.81-1.49c0.2-0.59,0.3-1.27,0.3-2.04c0-0.66-0.09-1.28-0.28-1.84c-0.19-0.56-0.45-1.05-0.78-1.47
                          c-0.33-0.41-0.73-0.74-1.2-0.97c-0.46-0.23-0.97-0.35-1.52-0.35h-1.41V46.2z"/>
                      <path class="st1" d="M40.14,35.62h4.05c0.69,0,1.3,0.08,1.83,0.24c0.52,0.16,0.96,0.38,1.31,0.67c0.35,0.29,0.61,0.63,0.78,1.02
                          c0.17,0.39,0.26,0.82,0.26,1.28c0,0.33-0.06,0.65-0.18,0.95c-0.12,0.3-0.29,0.58-0.5,0.83c-0.22,0.25-0.48,0.47-0.78,0.67
                          c-0.3,0.19-0.64,0.36-1,0.48l3.83,5.65h-2.87l-3.62-5.34h-0.47v5.34h-2.64V35.62z M42.78,41.05h0.58c0.32,0,0.62-0.05,0.9-0.15
                          c0.28-0.1,0.51-0.24,0.72-0.43c0.2-0.19,0.36-0.41,0.48-0.68c0.12-0.27,0.17-0.57,0.17-0.91c0-0.31-0.04-0.59-0.13-0.85
                          c-0.09-0.26-0.21-0.48-0.37-0.67c-0.16-0.19-0.34-0.34-0.56-0.44c-0.21-0.1-0.45-0.16-0.71-0.16h-1.07V41.05z"/>
                      <path class="st1" d="M57.28,47.67c-0.58,0-1.13-0.07-1.66-0.21c-0.53-0.14-1.02-0.34-1.47-0.6c-0.45-0.26-0.87-0.57-1.24-0.94
                          c-0.37-0.37-0.69-0.79-0.95-1.25c-0.26-0.46-0.46-0.97-0.61-1.51c-0.14-0.54-0.22-1.12-0.22-1.73c0-0.6,0.07-1.16,0.22-1.69
                          c0.14-0.53,0.35-1.02,0.61-1.47c0.26-0.45,0.58-0.85,0.95-1.21c0.37-0.36,0.79-0.67,1.24-0.92c0.45-0.25,0.94-0.44,1.47-0.57
                          c0.53-0.14,1.08-0.2,1.66-0.2c0.58,0,1.13,0.07,1.66,0.2c0.53,0.14,1.02,0.33,1.47,0.58c0.45,0.25,0.87,0.56,1.24,0.92
                          c0.37,0.36,0.69,0.76,0.95,1.21c0.26,0.45,0.47,0.94,0.61,1.46c0.14,0.53,0.22,1.09,0.22,1.68c0,0.62-0.07,1.21-0.22,1.76
                          c-0.14,0.55-0.35,1.06-0.61,1.52c-0.26,0.46-0.58,0.88-0.95,1.25c-0.37,0.37-0.79,0.68-1.24,0.94
                          c-0.45,0.26-0.95,0.45-1.47,0.59C58.41,47.61,57.86,47.67,57.28,47.67 M57.28,46.67c0.52,0,0.99-0.13,1.39-0.39
                          c0.4-0.26,0.74-0.63,1.01-1.09c0.27-0.47,0.48-1.02,0.62-1.66c0.14-0.64,0.21-1.34,0.21-2.09c0-0.75-0.07-1.43-0.21-2.05
                          c-0.14-0.62-0.34-1.15-0.62-1.6c-0.27-0.45-0.61-0.8-1.01-1.04c-0.4-0.25-0.86-0.37-1.39-0.37c-0.53,0-1,0.12-1.4,0.37
                          c-0.4,0.25-0.74,0.6-1.01,1.04c-0.27,0.45-0.47,0.98-0.61,1.6c-0.14,0.62-0.21,1.31-0.21,2.05c0,0.76,0.07,1.45,0.21,2.09
                          c0.14,0.64,0.34,1.19,0.61,1.66c0.27,0.47,0.61,0.83,1.01,1.09C56.28,46.54,56.75,46.67,57.28,46.67"/>
                      <path class="st1" d="M71.8,35.62h3.91c1,0,1.92,0.13,2.77,0.38c0.84,0.26,1.57,0.63,2.17,1.13c0.61,0.49,1.08,1.1,1.42,1.82
                          c0.34,0.72,0.51,1.54,0.51,2.46c0,0.83-0.14,1.61-0.43,2.34c-0.28,0.73-0.7,1.37-1.25,1.91c-0.55,0.54-1.22,0.97-2.02,1.28
                          c-0.8,0.31-1.71,0.47-2.75,0.47H71.8V35.62z M74.44,46.2h1.41c0.52,0,1.01-0.1,1.47-0.31c0.46-0.21,0.86-0.52,1.2-0.92
                          c0.34-0.41,0.61-0.9,0.81-1.49c0.2-0.59,0.3-1.27,0.3-2.04c0-0.66-0.09-1.28-0.28-1.84c-0.19-0.56-0.45-1.05-0.78-1.47
                          c-0.34-0.41-0.74-0.74-1.2-0.97c-0.46-0.23-0.97-0.35-1.52-0.35h-1.41V46.2z"/>
                      <polygon class="st1" points="92.8,35.62 92.8,36.62 87.41,36.62 87.41,40.51 91.89,40.51 91.89,41.52 87.41,41.52 87.41,46.2 
                      92.8,46.2 92.8,47.41 84.77,47.41 84.77,35.62 				"/>
                      <polygon class="st1" points="103.35,35.62 106.91,43.77 110.21,35.62 111.91,35.62 107.06,47.59 105.82,47.59 100.62,35.62 				
                          	"/>
                      <path class="st1" d="M118.13,35.36l5.04,12.05h-2.7l-1.34-3.22h-4.18l-1.41,3.22h-1.8l5.25-12.05H118.13z M115.44,43.09h3.24
                          l-1.6-3.81L115.44,43.09z"/>
                      <polygon class="st1" points="125.57,35.62 128.21,35.62 128.21,46.2 133.7,46.2 133.7,47.41 125.57,47.41 				"/>
                      <path class="st1" d="M136.25,35.62h3.91c1,0,1.92,0.13,2.77,0.38c0.84,0.26,1.57,0.63,2.17,1.13c0.61,0.49,1.08,1.1,1.42,1.82
                          c0.34,0.72,0.51,1.54,0.51,2.46c0,0.83-0.14,1.61-0.43,2.34c-0.28,0.73-0.7,1.37-1.25,1.91c-0.55,0.54-1.22,0.97-2.02,1.28
                          c-0.8,0.31-1.72,0.47-2.75,0.47h-4.32V35.62z M138.89,46.2h1.41c0.52,0,1.01-0.1,1.47-0.31c0.46-0.21,0.86-0.52,1.2-0.92
                          c0.34-0.41,0.61-0.9,0.81-1.49c0.2-0.59,0.3-1.27,0.3-2.04c0-0.66-0.09-1.28-0.28-1.84c-0.19-0.56-0.45-1.05-0.78-1.47
                          c-0.34-0.41-0.74-0.74-1.2-0.97c-0.46-0.23-0.97-0.35-1.52-0.35h-1.41V46.2z"/>
                      <rect x="150.13" y="35.62" class="st1" width="2.64" height="11.79"/>
                      <polygon class="st1" points="158.1,35.62 161.66,43.77 164.95,35.62 166.65,35.62 161.81,47.59 160.57,47.59 155.37,35.62 				
                          	"/>
                      <rect x="168.77" y="35.62" class="st1" width="2.64" height="11.79"/>
                      <path class="st1" d="M180.41,35.36l5.04,12.05h-2.7l-1.35-3.22h-4.18l-1.41,3.22h-1.8l5.25-12.05H180.41z M177.72,43.09h3.24
                          l-1.6-3.81L177.72,43.09z"/>
                    </g>
                    <g>
                      <path class="st0" d="M1.04,19.65c0-0.09,0.08-0.18,0.18-0.18h2.24c1.17,0,2.14,0.95,2.14,2.11c0,1.18-0.96,2.15-2.13,2.15H2.28
                          v2.24c0,0.1-0.09,0.18-0.18,0.18H1.22c-0.11,0-0.18-0.09-0.18-0.18V19.65z M3.38,22.55c0.53,0,0.98-0.43,0.98-0.99
                          c0-0.52-0.45-0.92-0.98-0.92H2.28v1.92H3.38z"/>
                      <path class="st0" d="M13.36,19.65c0-0.09,0.08-0.18,0.18-0.18h2.73c1.14,0,2.08,0.92,2.08,2.05c0,0.88-0.58,1.59-1.41,1.93
                          l1.31,2.42c0.07,0.12,0,0.28-0.16,0.28h-1c-0.09,0-0.13-0.05-0.15-0.09l-1.27-2.53H14.6v2.43c0,0.1-0.09,0.18-0.18,0.18h-0.88
                          c-0.1,0-0.18-0.09-0.18-0.18V19.65z M16.17,22.51c0.51,0,0.94-0.44,0.94-0.97c0-0.51-0.44-0.93-0.94-0.93h-1.55v1.9H16.17z"/>
                      <path class="st0" d="M26.4,19.65c0-0.09,0.08-0.18,0.18-0.18h3.88c0.1,0,0.18,0.09,0.18,0.18v0.78c0,0.09-0.08,0.18-0.18,0.18
                          h-2.82v1.56h2.36c0.1,0,0.18,0.09,0.18,0.18v0.79c0,0.1-0.09,0.18-0.18,0.18h-2.36V25h2.82c0.1,0,0.18,0.09,0.18,0.18v0.78
                          c0,0.1-0.08,0.18-0.18,0.18h-3.88c-0.11,0-0.18-0.09-0.18-0.18V19.65z"/>
                      <path class="st0" d="M38.59,19.65c0-0.09,0.09-0.18,0.18-0.18h0.91c0.1,0,0.18,0.09,0.18,0.18v3.9c0,0.81,0.55,1.45,1.38,1.45
                          c0.83,0,1.39-0.64,1.39-1.44v-3.91c0-0.09,0.08-0.18,0.18-0.18h0.91c0.1,0,0.18,0.09,0.18,0.18v3.97c0,1.45-1.15,2.62-2.66,2.62
                          c-1.5,0-2.65-1.17-2.65-2.62V19.65z"/>
                      <path class="st0" d="M52.22,19.54c0-0.1,0.09-0.17,0.18-0.17h0.24l3.97,4.22h0.01v-3.94c0-0.09,0.08-0.18,0.18-0.18h0.88
                          c0.1,0,0.18,0.09,0.18,0.18v6.42c0,0.1-0.09,0.17-0.18,0.17h-0.23l-3.99-4.38h-0.01v4.1c0,0.1-0.08,0.18-0.18,0.18H52.4
                          c-0.1,0-0.18-0.09-0.18-0.18V19.54z"/>
                      <path class="st0" d="M66.28,19.65c0-0.09,0.09-0.18,0.18-0.18h0.89c0.09,0,0.18,0.09,0.18,0.18v6.32c0,0.1-0.09,0.18-0.18,0.18
                          h-0.89c-0.1,0-0.18-0.09-0.18-0.18V19.65z"/>
                      <path class="st0" d="M75.08,19.71c-0.06-0.12,0.02-0.25,0.16-0.25h0.97c0.09,0,0.14,0.06,0.16,0.1l1.83,4.1h0.07l1.83-4.1
                          c0.02-0.05,0.08-0.1,0.16-0.1h0.97c0.14,0,0.22,0.12,0.16,0.25l-2.96,6.42c-0.03,0.06-0.08,0.1-0.16,0.1h-0.1
                          c-0.09,0-0.13-0.05-0.16-0.1L75.08,19.71z"/>
                      <path class="st0" d="M88.95,19.65c0-0.09,0.08-0.18,0.18-0.18h3.88c0.11,0,0.18,0.09,0.18,0.18v0.78c0,0.09-0.08,0.18-0.18,0.18
                          h-2.82v1.56h2.36c0.1,0,0.18,0.09,0.18,0.18v0.79c0,0.1-0.09,0.18-0.18,0.18h-2.36V25h2.82c0.11,0,0.18,0.09,0.18,0.18v0.78
                          c0,0.1-0.08,0.18-0.18,0.18h-3.88c-0.1,0-0.18-0.09-0.18-0.18V19.65z"/>
                      <path class="st0" d="M101.24,19.65c0-0.09,0.08-0.18,0.18-0.18h2.73c1.14,0,2.08,0.92,2.08,2.05c0,0.88-0.58,1.59-1.41,1.93
                          l1.31,2.42c0.07,0.12,0,0.28-0.16,0.28h-1c-0.09,0-0.13-0.05-0.15-0.09l-1.27-2.53h-1.06v2.43c0,0.1-0.09,0.18-0.18,0.18h-0.88
                          c-0.1,0-0.18-0.09-0.18-0.18V19.65z M104.04,22.51c0.51,0,0.94-0.44,0.94-0.97c0-0.51-0.44-0.93-0.94-0.93h-1.55v1.9H104.04z"/>
                      <path class="st0" d="M113.88,25.21l0.34-0.59c0.08-0.13,0.24-0.13,0.32-0.07c0.05,0.03,0.82,0.59,1.44,0.59
                          c0.49,0,0.87-0.33,0.87-0.73c0-0.49-0.41-0.82-1.21-1.15c-0.9-0.36-1.79-0.94-1.79-2.06c0-0.85,0.63-1.83,2.15-1.83
                          c0.97,0,1.72,0.5,1.91,0.64c0.1,0.06,0.12,0.22,0.06,0.31l-0.36,0.54c-0.08,0.11-0.22,0.19-0.33,0.11
                          c-0.08-0.05-0.8-0.52-1.32-0.52c-0.55,0-0.84,0.36-0.84,0.67c0,0.45,0.35,0.75,1.13,1.07c0.92,0.37,1.99,0.93,1.99,2.16
                          c0,0.98-0.85,1.89-2.19,1.89c-1.2,0-1.91-0.56-2.1-0.74C113.84,25.41,113.79,25.36,113.88,25.21"/>
                      <path class="st0" d="M126.12,19.65c0-0.09,0.09-0.18,0.18-0.18h0.89c0.09,0,0.18,0.09,0.18,0.18v6.32c0,0.1-0.09,0.18-0.18,0.18
                          h-0.89c-0.1,0-0.18-0.09-0.18-0.18V19.65z"/>
                      <path class="st0" d="M136.68,20.61h-1.46c-0.1,0-0.18-0.09-0.18-0.18v-0.78c0-0.09,0.08-0.18,0.18-0.18h4.17
                          c0.1,0,0.18,0.09,0.18,0.18v0.78c0,0.09-0.08,0.18-0.18,0.18h-1.46v5.35c0,0.1-0.09,0.18-0.18,0.18h-0.89
                          c-0.1,0-0.18-0.09-0.18-0.18V20.61z"/>
                      <path class="st0" d="M146.01,25.89l2.97-6.42c0.03-0.06,0.08-0.1,0.16-0.1h0.1c0.1,0,0.13,0.05,0.16,0.1l2.94,6.42
                          c0.06,0.12-0.02,0.25-0.16,0.25h-0.83c-0.14,0-0.21-0.06-0.28-0.19l-0.47-1.03h-2.85l-0.47,1.03c-0.04,0.1-0.12,0.19-0.28,0.19
                          h-0.83C146.03,26.14,145.96,26.02,146.01,25.89 M150.13,23.86l-0.95-2.1h-0.03l-0.93,2.1H150.13z"/>
                      <path class="st0" d="M159.88,19.65c0-0.09,0.08-0.18,0.18-0.18h2.73c1.14,0,2.08,0.92,2.08,2.05c0,0.88-0.58,1.59-1.41,1.93
                          l1.31,2.42c0.07,0.12,0,0.28-0.16,0.28h-1c-0.09,0-0.13-0.05-0.15-0.09l-1.27-2.53h-1.06v2.43c0,0.1-0.08,0.18-0.18,0.18h-0.88
                          c-0.1,0-0.18-0.09-0.18-0.18V19.65z M162.69,22.51c0.51,0,0.94-0.44,0.94-0.97c0-0.51-0.44-0.93-0.94-0.93h-1.55v1.9H162.69z"/>
                      <path class="st0" d="M172.92,19.65c0-0.09,0.09-0.18,0.18-0.18h0.89c0.09,0,0.18,0.09,0.18,0.18v6.32c0,0.1-0.09,0.18-0.18,0.18
                          h-0.89c-0.1,0-0.18-0.09-0.18-0.18V19.65z"/>
                      <path class="st0" d="M185.53,19.37c1.91,0,3.43,1.54,3.43,3.44c0,1.91-1.53,3.42-3.43,3.42c-1.91,0-3.42-1.52-3.42-3.42
                          C182.11,20.9,183.62,19.37,185.53,19.37 M185.53,25c1.2,0,2.19-0.98,2.19-2.18c0-1.2-0.99-2.2-2.19-2.2c-1.2,0-2.18,1-2.18,2.2
                          C183.35,24.01,184.33,25,185.53,25"/>
                    </g>
                  </g>
                </g>
              </g>
            </svg>
          </div>

          <!-- Main Content -->
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 30px;">
            
            <!-- Left Column: Payment Information -->
            <div>
              <h2 style="font-size: 18px; font-weight: bold; color: #000; margin: 0 0 20px 0;">Información de pago</h2>
              
              <div style="display: flex; flex-direction: column; gap: 12px;">
                <div>
                  <span style="color: #666; font-size: 14px;">N° Orden de pedido</span>
                  <div style="color: #000; font-weight: 600; font-size: 16px; margin-top: 2px;">${orderData.buyOrder || 'N/A'}</div>
                </div>
                
                <div>
                  <span style="color: #666; font-size: 14px;">Nombre del comercio</span>
                  <div style="color: #000; font-weight: 600; font-size: 16px; margin-top: 2px;">Preuniversitario Pedro de Valdivia SA</div>
                </div>
                
                <div>
                  <span style="color: #666; font-size: 14px;">Monto y moneda</span>
                  <div style="color: #000; font-weight: 600; font-size: 16px; margin-top: 2px;">$${this.formatCurrency(pricing.precioTotal)}</div>
                </div>
                
                <div>
                  <span style="color: #666; font-size: 14px;">Código de cupón</span>
                  <div style="color: #000; font-weight: 600; font-size: 16px; margin-top: 2px;">${orderData.coupon || 'Sin cupón'}</div>
                </div>
                
                <div>
                  <span style="color: #666; font-size: 14px;">Descuento aplicado</span>
                  <div style="color: #000; font-weight: 600; font-size: 16px; margin-top: 2px;">${this.calculateCouponDiscountPercentage()}%</div>
                </div>
                
                <div>
                  <span style="color: #666; font-size: 14px;">Fecha de transacción</span>
                  <div style="color: #000; font-weight: 600; font-size: 16px; margin-top: 2px;">${this.formatDate(orderData.createdAt)}</div>
                </div>
                
                <div>
                  <span style="color: #666; font-size: 14px;">Descripción de los bienes y/o servicios</span>
                  <div style="color: #000; font-weight: 600; font-size: 16px; margin-top: 2px;">Programas PAES</div>
                </div>
              </div>
            </div>

            <!-- Right Column: Purchase Information -->
            <div>
              <h2 style="font-size: 18px; font-weight: bold; color: #000; margin: 0 0 20px 0;">Información de tu compra</h2>
              
              <!-- Customer Information -->
              <div style="display: flex; flex-direction: column; gap: 12px; margin-bottom: 20px;">
                <div style="display: flex; align-items: center; gap: 12px;">
                  <span style="color: #666; font-size: 16px; width: 20px; text-align: center;">📄</span>
                  <div style="flex: 1;">
                    <span style="color: #666; font-size: 14px;">N° Orden de pedido</span>
                    <div style="color: #000; font-weight: 600; font-size: 16px; margin-top: 2px;">${orderData.buyOrder || 'N/A'}</div>
                  </div>
                </div>
                
                <div style="display: flex; align-items: center; gap: 12px;">
                  <span style="color: #666; font-size: 16px; width: 20px; text-align: center;">👤</span>
                  <div style="flex: 1;">
                    <span style="color: #666; font-size: 14px;">Nombre del alumno</span>
                    <div style="color: #000; font-weight: 600; font-size: 16px; margin-top: 2px;">${studentName}</div>
                  </div>
                </div>
                
                <div style="display: flex; align-items: center; gap: 12px;">
                  <span style="color: #666; font-size: 16px; width: 20px; text-align: center;">📍</span>
                  <div style="flex: 1;">
                    <span style="color: #666; font-size: 14px;">Sede</span>
                    <div style="color: #000; font-weight: 600; font-size: 16px; margin-top: 2px;">${locationName}</div>
                  </div>
                </div>
              </div>

              <!-- Separator -->
              <div style="border-bottom: 1px dashed #ccc; margin: 20px 0;"></div>

              <!-- Cursos -->
              <div style="margin-bottom: 20px;">
                <h3 style="font-size: 16px; font-weight: bold; color: #000; margin: 0 0 12px 0;">Cursos</h3>
                <div style="display: flex; flex-direction: column; gap: 12px;">
                  ${this.getGroupedCourses(selectedCourses).map((course) => {
                    const color = this.getCourseColor(course.name);
                    const courseName = this.cleanCourseName(course.name);
                    const schedules = course.schedules.join(', ');
                    return `
                      <div style="display: flex; align-items: flex-start; gap: 12px;">
                        <div style="width: 12px; height: 12px; border-radius: 50%; background-color: ${color}; flex-shrink: 0; margin-top: 2px;"></div>
                        <div style="flex: 1; min-width: 0;">
                          <div style="color: #000; font-weight: 600; font-size: 14px; line-height: 1.3; margin-bottom: 4px;">${courseName}</div>
                          <div style="color: #666; font-size: 12px; line-height: 1.2;">${schedules}</div>
                        </div>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>

              <!-- Separator -->
              <div style="border-bottom: 1px dashed #ccc; margin: 20px 0;"></div>

              <!-- Cost Summary -->
              <div style="display: flex; flex-direction: column; gap: 8px; margin-bottom: 20px;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <span style="color: #666; font-size: 14px;">Total cursos</span>
                  <span style="color: #000; font-weight: 600; font-size: 14px;">$${this.formatCurrency(pricing.precioSubTotal)}</span>
                </div>
                
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <span style="color: #10b981; font-size: 14px;">Desc. Inscripción online (${this.calculateDiscountPercentage()}%)</span>
                  <span style="color: #10b981; font-weight: 600; font-size: 14px;">-$${this.formatCurrency(pricing.valorDescuento)}</span>
                </div>
                
                ${pricing.valorDescuentoCupon > 0 ? `
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <span style="color: #10b981; font-size: 14px;">Desc. Cupón ${this.getCouponCode()} (${this.calculateCouponDiscountPercentage()}%)</span>
                  <span style="color: #10b981; font-weight: 600; font-size: 14px;">-$${this.formatCurrency(pricing.valorDescuentoCupon)}</span>
                </div>
                ` : ''}
                
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <span style="color: #666; font-size: 14px;">Matrícula online</span>
                  <span style="color: #000; font-weight: 600; font-size: 14px;">$${this.formatCurrency(pricing.valorMatricula)}</span>
                </div>
              </div>

              <!-- Separator -->
              <div style="border-bottom: 1px dashed #ccc; margin: 20px 0;"></div>

              <!-- Total -->
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="color: #000; font-weight: 600; font-size: 16px;">Monto programa</span>
                <span style="color: #000; font-weight: 600; font-size: 16px;">$${this.formatCurrency(pricing.precioTotal)}</span>
              </div>
            </div>
          </div>

          <!-- Separator -->
          <div style="border-top: 1px solid #000; margin: 30px 0;"></div>

          <!-- Total Paid Section -->
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px;">
            <span style="color: #000; font-weight: 600; font-size: 18px;">Total pagado</span>
            <span style="color: #000; font-weight: 600; font-size: 18px;">$${this.formatCurrency(pricing.precioTotal)}</span>
          </div>

          <!-- Separator -->
          <div style="border-bottom: 1px dashed #ccc; margin: 30px 0;"></div>

          <!-- Footer -->
          <div style="background: #f5f5f5; padding: 20px; text-align: center;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
              <span style="color: #000; font-size: 12px;">Preuniversitario Pedro de Valdivia. 2023</span>
              <span style="color: #000; font-size: 12px;">https://preupdv.cl/</span>
            </div>
            <div style="color: #000; font-size: 12px; margin-bottom: 4px;">¿Necesitas ayuda, llámanos</div>
            <div style="color: #000; font-weight: bold; font-size: 14px; display: flex; align-items: center; justify-content: center; gap: 4px;">
              <span style="font-size: 12px;">📞</span>
              600 500 2020
            </div>
          </div>
        </div>
      `;
    },

    getStudentCoursesFromOrderDetails() {
      // MISMA LÓGICA QUE PurchaseConfirmation.vue
      if(this.order?.OrderDetails && this.order.OrderDetails.length > 0){
        return this.order.OrderDetails.map(orderDetail => {
          const course = orderDetail.Course;
          const originalSubjectName = course?.name || '';
          
          // BUSCAR HORARIO EN sessionState.students (misma lógica que PurchaseConfirmation)
          let schedule = 'Horario no disponible';
          if(this.students && this.students.length > 0) {
            const student = this.students[0];
            if(student.selectedCourses) {
              const studentCourse = student.selectedCourses.find(sc => 
                String(sc.courseId) === String(course?.id) || String(sc.id) === String(course?.id)
              );
              
              if(studentCourse) {
                // Obtener horario del curso del estudiante
                if(studentCourse.hour) {
                  schedule = this.getSchedule(studentCourse.hoursPerDay);
                } else if(studentCourse.hoursPerDay) {
                  schedule = this.getSchedule(studentCourse.hoursPerDay);
                } else if(studentCourse.schedule) {
                  schedule = studentCourse.schedule;
                }
              }
            }
          }
          
          // VALIDAR SI ES ELECTIVO SOLO POR EL NOMBRE QUE CONTENGA "ELECTIVO"
          const isElectiveByName = originalSubjectName.toLowerCase().includes('electivo');
          
          const courseWithLabels = {
            ...course,
            hasMention: course?.hasMention || false,
            isElective: isElectiveByName,
            isCommon: course?.isCommon || false
          };
          
          // Usar utilidad compartida para formatear nombre completo
          const formattedName = this.formatFullCourseName(originalSubjectName, courseWithLabels);
          
          return {
            name: formattedName,
            schedule: schedule,
            courseId: course?.id || orderDetail.courseId,
            originalName: originalSubjectName
          };
        });
      }
      return [];
    },

    getStudentLocation() {
      // Obtener sede directamente de los datos de la API (order.sessionState.students)
      if(this.order && this.order.sessionState && this.order.sessionState.students && this.order.sessionState.students.length > 0) {
        const student = this.order.sessionState.students[0];
        if(student.selectedLocation) {
          return getLocationDisplayName(student.selectedLocation);
        } else if(student.preferredVenue) {
          return getLocationDisplayName(student.preferredVenue);
        }
      }
      return 'Sede no especificada';
    },

    getSchedule(hoursPerDay){
      if(!hoursPerDay || hoursPerDay.length === 0) return '';
      
      let days = "";
      if (hoursPerDay.length == 1) {
        days = hoursPerDay[0].day + " " + hoursPerDay[0].startTime.slice(0, -3) + " a " + hoursPerDay[0].endTime.slice(0, -3);
      } else if (hoursPerDay.length == 2) {
        if (hoursPerDay[0].startTime.slice(0, -3) == hoursPerDay[1].startTime.slice(0, -3) && hoursPerDay[0].endTime.slice(0, -3) == hoursPerDay[1].endTime.slice(0, -3)) {
          days = hoursPerDay[0].day + " y " + hoursPerDay[1].day + " " + hoursPerDay[0].startTime.slice(0, -3) + " a " + hoursPerDay[0].endTime.slice(0, -3);
        } else {
          days = hoursPerDay[0].day + " " + hoursPerDay[0].startTime.slice(0, -3) + " a " + hoursPerDay[0].endTime.slice(0, -3) + " y " +hoursPerDay[1].day + " " + hoursPerDay[1].startTime.slice(0, -3) + " a " + hoursPerDay[1].endTime.slice(0, -3);
        }
      }
      return days;
    },

    formatFullCourseName(originalSubjectName, course) {
      // Usar utilidad centralizada para evitar duplicación de código
      return formatFullCourseName(originalSubjectName, course);
    },

    getGroupedCourses(selectedCourses) {
      const groupedCourses = {};
      
      if (!selectedCourses || !Array.isArray(selectedCourses)) {
        return [];
      }
      
      selectedCourses.forEach(course => {
        if (!course || !course.name) return;
        
        const courseName = course.name;
        
        if (!groupedCourses[courseName]) {
          groupedCourses[courseName] = {
            name: courseName,
            schedules: []
          };
        }
        
        if (course.schedule) {
          groupedCourses[courseName].schedules.push(course.schedule);
        }
      });
      
      return Object.values(groupedCourses);
    },

    getCourseColor(courseName) {
      // Usar la paleta global de colores
      return getRandomColor(courseName);
    },

    cleanCourseName(fullName) {
      if (!fullName || typeof fullName !== 'string') return '';
      let cleanName = fullName
        .replace(/\s*GE\s+(Tarde|Mañana)\s*\d*$/i, '')
        .replace(/\s*Tarde\s*\d*$/i, '')
        .replace(/\s*Mañana\s*\d*$/i, '')
        .replace(/\s*\d{3,4}$/, '')
        .trim();
      return cleanName || fullName;
    },

    calculatePricing() {
      if (!this.students || this.students.length === 0) {
        return {
          precioBruto: 0,
          precioSubTotal: 0,
          valorDescuento: 0,
          valorDescuentoCupon: 0,
          valorMatricula: 0,
          precioTotal: 0,
          porcentajeDescuento: 0
        };
      }
      
      const student = this.students[0];
      const prices = student?.prices || {};
      
      return {
        precioBruto: prices.precioBruto || 0,
        precioSubTotal: prices.precioSubTotal || 0,
        valorDescuento: prices.valorDescuento || 0,
        valorDescuentoCupon: prices.valorDescuentoCupon || 0,
        valorMatricula: prices.valorMatricula || 0,
        precioTotal: prices.precioTotal || 0,
        porcentajeDescuento: prices.valorDescuento && prices.precioSubTotal ? 
          Math.round((prices.valorDescuento / prices.precioSubTotal) * 100) : 0
      };
    },

    calculateDiscountPercentage() {
      // Calcular el porcentaje de descuento dinámicamente como en DataConfirmation
      if(this.students && this.students.length > 0 && this.students[0].prices){
        const prices = this.students[0].prices;
        const precioSubTotal = prices.precioSubTotal || 0;
        const valorDescuento = prices.valorDescuento || 0;
        
        if (precioSubTotal > 0) {
          return Math.round((valorDescuento / precioSubTotal) * 100);
        }
      }
      return 0;
    },

    calculateCouponDiscountPercentage() {
      // Calcular solo el porcentaje del cupón
      if(this.students && this.students.length > 0 && this.students[0].prices){
        const prices = this.students[0].prices;
        const precioSubTotal = prices.precioSubTotal || 0;
        const valorDescuento = prices.valorDescuento || 0;
        const valorDescuentoCupon = prices.valorDescuentoCupon || 0;
        
        // Calcular el subtotal después del primer descuento (matrícula online)
        const subtotalDespuesPrimerDescuento = precioSubTotal - valorDescuento;
        
        // El porcentaje del cupón debe calcularse sobre el subtotal restante
        if (subtotalDespuesPrimerDescuento > 0 && valorDescuentoCupon > 0) {
          return Math.round((valorDescuentoCupon / subtotalDespuesPrimerDescuento) * 100);
        }
      }
      return 0;
    },

    getCouponCode() {
      // Obtener el código del cupón desde los datos del estudiante
      if(this.students && this.students.length > 0 && this.students[0].prices){
        const cupon = this.students[0].prices.cupon;
        const validCupon = this.students[0].prices.validCupon;
        
        // Si hay cupón pero no es válido, mostrar "Sin cupón"
        if (cupon && !validCupon) {
          return 'Sin cupón';
        }
        
        // Si hay cupón y es válido, mostrarlo
        if (cupon && validCupon) {
          return cupon;
        }
        
        return 'Sin cupón';
      }
      // Fallback al cupón del order si no hay datos del estudiante
      return this.order?.coupon || 'Sin cupón';
    },

    formatCurrency(amount) {
      if (!amount && amount !== 0) return '0';
      const numAmount = Number(amount);
      if (isNaN(numAmount)) return '0';
      return numAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';
        
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
      } catch (error) {
        console.error('Error formateando fecha:', error);
        return 'N/A';
      }
    }
  }
}
</script>

<style scoped>
.action-link {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #3691D8;
  font-family: 'Poppins-Medium', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  text-decoration: none;
  transition: opacity 0.2s;
  white-space: nowrap;
  cursor: pointer;
}

.action-link:hover {
  opacity: 0.8;
}

.action-link img {
  width: 18px;
  height: 18px;
}

.action-link span {
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
}
</style>
